import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'
import PriorityCodes from '@/views/admin/PriorityCodes.vue'

// Import views
const Login = () => import(/* webpackChunkName: "login" */ '../views/Login.vue')
const AdminDashboard = () => import(/* webpackChunkName: "admin" */ '../views/admin/Dashboard.vue')
const AdminUsers = () => import(/* webpackChunkName: "admin" */ '../views/admin/Users.vue')
const AdminStudents = () => import(/* webpackChunkName: "admin" */ '../views/admin/Students.vue')
const AdminTransactionRequirements = () => import(/* webpackChunkName: "admin" */ '../views/admin/TransactionRequirements.vue')
const AdminTransactionManagement = () => import(/* webpackChunkName: "admin" */ '../views/admin/TransactionManagement.vue')
const AdminReports = () => import(/* webpackChunkName: "admin" */ '../views/admin/Reports.vue')
const AdminSettings = () => import(/* webpackChunkName: "admin" */ '../views/admin/Settings.vue')
const TellerDashboard = () => import(/* webpackChunkName: "teller" */ '../views/teller/Dashboard.vue')
const TellerQueue = () => import(/* webpackChunkName: "teller" */ '../views/teller/Queue.vue')
const TellerReports = () => import(/* webpackChunkName: "teller" */ '../views/teller/Reports.vue')
const RegistrarDashboard = () => import(/* webpackChunkName: "registrar" */ '../views/registrar/Dashboard.vue')
const RegistrarReports = () => import(/* webpackChunkName: "registrar" */ '../views/registrar/Reports.vue')
const NotFound = () => import(/* webpackChunkName: "errors" */ '../views/NotFound.vue')

// Kiosk views
const KioskWelcome = () => import(/* webpackChunkName: "kiosk" */ '../views/kiosk/Welcome.vue')
const KioskTransactionSelection = () => import(/* webpackChunkName: "kiosk" */ '../views/kiosk/TransactionSelection.vue')
const KioskStudentId = () => import(/* webpackChunkName: "kiosk" */ '../views/kiosk/StudentId.vue')
const KioskAmountInput = () => import(/* webpackChunkName: "kiosk" */ '../views/kiosk/AmountInput.vue')
const KioskStudentVerification = () => import(/* webpackChunkName: "kiosk" */ '../views/kiosk/StudentVerification.vue')
const KioskQueueNumber = () => import(/* webpackChunkName: "kiosk" */ '../views/kiosk/QueueNumber.vue')

// Display views
const QueueDisplay = () => import(/* webpackChunkName: "display" */ '../views/display/QueueDisplay.vue')

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  // Kiosk routes
  {
    path: '/kiosk',
    redirect: '/kiosk/welcome',
    meta: { requiresAuth: false, kiosk: true }
  },
  {
    path: '/kiosk/welcome',
    name: 'KioskWelcome',
    component: KioskWelcome,
    meta: { requiresAuth: false, kiosk: true }
  },
  {
    path: '/kiosk/transaction-selection',
    name: 'KioskTransactionSelection',
    component: KioskTransactionSelection,
    meta: { requiresAuth: false, kiosk: true }
  },
  {
    path: '/kiosk/student-id',
    name: 'KioskStudentId',
    component: KioskStudentId,
    meta: { requiresAuth: false, kiosk: true }
  },
  {
    path: '/kiosk/amount-input',
    name: 'KioskAmountInput',
    component: KioskAmountInput,
    meta: { requiresAuth: false, kiosk: true }
  },
  {
    path: '/kiosk/student-verification',
    name: 'KioskStudentVerification',
    component: KioskStudentVerification,
    meta: { requiresAuth: false, kiosk: true }
  },
  {
    path: '/kiosk/queue-number/:transactionId?',
    name: 'KioskQueueNumber',
    component: KioskQueueNumber,
    meta: { requiresAuth: false, kiosk: true },
    props: true
  },
  // Display routes (public - no authentication required)
  {
    path: '/display',
    redirect: '/display/queue',
    meta: { requiresAuth: false, display: true }
  },
  {
    path: '/display/queue/:id?',
    name: 'QueueDisplay',
    component: QueueDisplay,
    meta: { requiresAuth: false, display: true },
    props: true
  },
  // Admin routes
  {
    path: '/admin',
    redirect: '/admin/dashboard',
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsers,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/students',
    name: 'AdminStudents',
    component: AdminStudents,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/transaction-requirements',
    name: 'AdminTransactionRequirements',
    component: AdminTransactionRequirements,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/transaction-management',
    name: 'AdminTransactionManagement',
    component: AdminTransactionManagement,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/reports',
    name: 'AdminReports',
    component: AdminReports,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/settings',
    name: 'AdminSettings',
    component: AdminSettings,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/priority-codes',
    name: 'PriorityCodes',
    component: PriorityCodes,
    meta: { 
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  // Teller routes
  {
    path: '/teller',
    redirect: '/teller/dashboard',
    meta: { requiresAuth: true, role: 'teller' }
  },
  {
    path: '/teller/dashboard',
    name: 'TellerDashboard',
    component: TellerDashboard,
    meta: { requiresAuth: true, role: 'teller' }
  },
  {
    path: '/teller/queue',
    name: 'TellerQueue',
    component: TellerQueue,
    meta: { requiresAuth: true, role: 'teller' }
  },
  {
    path: '/teller/reports',
    name: 'TellerReports',
    component: TellerReports,
    meta: { requiresAuth: true, role: 'teller' }
  },
  // Registrar routes
  {
    path: '/registrar',
    redirect: '/registrar/dashboard',
    meta: { requiresAuth: true, role: 'registrar' }
  },
  {
    path: '/registrar/dashboard',
    name: 'RegistrarDashboard',
    component: RegistrarDashboard,
    meta: { requiresAuth: true, role: 'registrar' }
  },
  {
    path: '/registrar/reports',
    name: 'RegistrarReports',
    component: RegistrarReports,
    meta: { requiresAuth: true, role: 'registrar' }
  },
  // 404 route
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: 'active'
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  // Check if the route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  
  if (!requiresAuth) {
    // If the route doesn't require auth, proceed
    return next()
  }
  
  // Check if user is authenticated
  const isAuthenticated = store.getters['auth/isAuthenticated']
  
  if (!isAuthenticated) {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token')
    
    if (token) {
      try {
        // Try to load user data to verify token is valid
        await store.dispatch('auth/getCurrentUser')
        
        // Check role for this route
        const roleRequired = to.matched.find(record => record.meta.role)?.meta.role
        const userRole = store.getters['auth/userRole']
        
        if (roleRequired && roleRequired !== userRole) {
          // User doesn't have the required role, redirect to their dashboard
          return next({ path: `/${userRole}/dashboard` })
        }
        
        // User is authenticated and has correct role
        return next()
      } catch (error) {
        // Token is invalid, redirect to login
        return next({ path: '/login' })
      }
    } else {
      // No token, redirect to login
      return next({ path: '/login' })
    }
  } else {
    // User is already authenticated, check role
    const roleRequired = to.matched.find(record => record.meta.role)?.meta.role
    const userRole = store.getters['auth/userRole']
    
    if (roleRequired && roleRequired !== userRole) {
      // User doesn't have the required role, redirect to their dashboard
      return next({ path: `/${userRole}/dashboard` })
    }
    
    // User is authenticated and has correct role
    return next()
  }
})

export default router 